import { Button, Flex, Form, InputNumber, Modal, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { FacilityNurseDto } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { useToast } from '../../hooks/use-toast';

import {
    DefaultRatesFormValues,
    toDefaultRatesDto,
    toFormValues,
} from './nurse-default-rates-values';

const EMPTY_VALUES: DefaultRatesFormValues = {
    hourlyRate: null,
    onCallRate: null,
    callbackRate: null,
    travelStipendEnabled: false,
    travelStipendAmount: null,
    travelStipendMinimumDistance: null,
};

const rateRules = [
    { type: 'number' as const, min: 0, message: 'Enter $0 or more.' },
];

export default function NurseDefaultRatesModal({
    facilityId,
    nurse,
    onClose,
}: {
    facilityId: string;
    nurse: Pick<FacilityNurseDto, 'id' | 'firstName' | 'lastName'>;
    onClose: () => void;
}) {
    const api = useApiClient();
    const { notification } = useToast();
    const [form] = Form.useForm<DefaultRatesFormValues>();
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const isStipendEnabled = Form.useWatch('travelStipendEnabled', form);

    useEffect(() => {
        if (!api) {
            return;
        }

        setLoading(true);
        api.facilityNurseDefaultRatesControllerGetDefaultRates({
            facilityId,
            profileId: nurse.id,
        })
            .then((rates) => form.setFieldsValue(toFormValues(rates)))
            .catch((err) =>
                notification.error({ message: unwrapError(err), duration: 3 })
            )
            .finally(() => setLoading(false));
    }, [api, facilityId, nurse.id]);

    const save = async (values: DefaultRatesFormValues, message: string) => {
        if (!api) {
            return;
        }

        setSaving(true);
        try {
            await api.facilityNurseDefaultRatesControllerSaveDefaultRates({
                facilityId,
                profileId: nurse.id,
                facilityNurseDefaultRatesDto: toDefaultRatesDto(values),
            });
            notification.success({ message, duration: 3 });
            setOpen(false);
        } catch (err) {
            notification.error({ message: unwrapError(err), duration: 3 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={`Default rates for ${nurse.firstName} ${nurse.lastName}`}
            loading={loading}
            open={open}
            onCancel={() => setOpen(false)}
            afterClose={onClose}
            footer={
                <Flex justify="space-between">
                    <Button
                        danger
                        disabled={saving}
                        onClick={() =>
                            void save(EMPTY_VALUES, 'Default rates removed')
                        }
                    >
                        Remove
                    </Button>
                    <Flex gap="small">
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={saving}
                            onClick={() =>
                                void form
                                    .validateFields()
                                    .then((values) =>
                                        save(values, 'Default rates saved')
                                    )
                            }
                        >
                            Save
                        </Button>
                    </Flex>
                </Flex>
            }
        >
            <Form form={form} layout="vertical" initialValues={EMPTY_VALUES}>
                <Form.Item
                    label="Default hourly rate"
                    name="hourlyRate"
                    rules={rateRules}
                >
                    <InputNumber prefix="$" addonAfter="per hour" />
                </Form.Item>
                <Flex gap="middle">
                    <Form.Item
                        label="On-call rate"
                        name="onCallRate"
                        rules={rateRules}
                    >
                        <InputNumber prefix="$" />
                    </Form.Item>
                    <Form.Item
                        label="Callback rate"
                        name="callbackRate"
                        rules={rateRules}
                    >
                        <InputNumber prefix="$" />
                    </Form.Item>
                </Flex>
                <Form.Item
                    label="Travel stipend"
                    name="travelStipendEnabled"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                {isStipendEnabled && (
                    <Flex gap="middle">
                        <Form.Item
                            label="Stipend amount"
                            name="travelStipendAmount"
                            rules={[
                                {
                                    required: true,
                                    message: 'Enter the stipend amount.',
                                },
                                {
                                    type: 'number',
                                    min: 1,
                                    message: 'Enter at least $1.',
                                },
                            ]}
                        >
                            <InputNumber prefix="$" />
                        </Form.Item>
                        <Form.Item
                            label="Applies at"
                            name="travelStipendMinimumDistance"
                            rules={[
                                {
                                    required: true,
                                    message: 'Enter the distance.',
                                },
                                {
                                    type: 'number',
                                    min: 1,
                                    message: 'Enter at least 1 mile.',
                                },
                            ]}
                        >
                            <InputNumber addonAfter="+ miles" />
                        </Form.Item>
                    </Flex>
                )}
            </Form>
        </Modal>
    );
}
